import type { ColorState, Direction, PlayerState, SnakeState } from "../types/state";

export interface UiEvents {
  onCreateRoom(): void;
  onEnterRoom(code: string): void;
  onJoin(name: string, colorId: number): void;
  onLeave(): void;
  onDirection(direction: Direction): void;
  onSpeed(level: number): void;
  onWalls(enabled: boolean): void;
  onStart(): void;
  onRestart(): void;
}

export class UiController {
  private readonly events: UiEvents;
  private readonly roomView = this.byId<HTMLElement>("roomView");
  private readonly roomInfoView = this.byId<HTMLElement>("roomInfoView");
  private readonly joinView = this.byId<HTMLElement>("joinView");
  private readonly gameView = this.byId<HTMLElement>("gameView");
  private readonly connectionPill = this.byId<HTMLElement>("connectionPill");
  private readonly roomCodeInput = this.byId<HTMLInputElement>("roomCodeInput");
  private readonly createRoomButton = this.byId<HTMLButtonElement>("createRoomButton");
  private readonly enterRoomButton = this.byId<HTMLButtonElement>("enterRoomButton");
  private readonly roomMessage = this.byId<HTMLElement>("roomMessage");
  private readonly roomCodeText = this.byId<HTMLElement>("roomCodeText");
  private readonly shareLinkInput = this.byId<HTMLInputElement>("shareLinkInput");
  private readonly copyLinkButton = this.byId<HTMLButtonElement>("copyLinkButton");
  private readonly colorGrid = this.byId<HTMLElement>("colorGrid");
  private readonly nameInput = this.byId<HTMLInputElement>("nameInput");
  private readonly joinButton = this.byId<HTMLButtonElement>("joinButton");
  private readonly joinMessage = this.byId<HTMLElement>("joinMessage");
  private readonly statusBox = this.byId<HTMLElement>("statusBox");
  private readonly statusLine = this.byId<HTMLElement>("statusLine");
  private readonly lobbyList = this.byId<HTMLElement>("lobbyList");
  private readonly hostControls = this.byId<HTMLElement>("hostControls");
  private readonly settingsHint = this.byId<HTMLElement>("settingsHint");
  private readonly startButton = this.byId<HTMLButtonElement>("startButton");
  private readonly restartButton = this.byId<HTMLButtonElement>("restartButton");
  private readonly leaveButton = this.byId<HTMLButtonElement>("leaveButton");
  private readonly resultCard = this.byId<HTMLElement>("resultCard");
  private readonly resultSummary = this.byId<HTMLElement>("resultSummary");
  private readonly canvasMount = this.byId<HTMLElement>("canvasMount");
  private readonly speedButtons = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-speed]"));
  private readonly wallButtons = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-walls]"));
  private readonly dirButtons = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-dir]"));

  private selectedColor = 0;
  private myIndex = -1;
  private state: SnakeState | null = null;
  private roomCode = "";
  private shareUrl = "";
  private online = false;
  private touchStartX = 0;
  private touchStartY = 0;

  public constructor(events: UiEvents) {
    this.events = events;
    this.bindEvents();
    this.render();
  }

  public setRoom(roomCode: string, shareUrl: string): void {
    this.roomCode = this.sanitizeRoomCode(roomCode);
    this.shareUrl = shareUrl;
    this.roomCodeText.textContent = this.roomCode || "-";
    this.shareLinkInput.value = this.shareUrl;
    this.render();
  }

  public clearRoom(): void {
    this.roomCode = "";
    this.shareUrl = "";
    this.state = null;
    this.myIndex = -1;
    this.roomCodeText.textContent = "-";
    this.shareLinkInput.value = "";
    this.setConnection(false, "escolha sala");
    this.render();
  }

  public setConnection(online: boolean, label: string): void {
    this.online = online;
    this.connectionPill.textContent = label;
    this.connectionPill.style.borderColor = online ? "#A6E3A1" : "#F9E2AF";
    this.joinButton.disabled = !online;
    this.joinButton.textContent = online ? "ENTRAR" : "AGUARDANDO...";
  }

  public setMessage(message: string): void {
    this.joinMessage.textContent = message;
    if (!this.roomCode) this.roomMessage.textContent = message;
  }

  public setMyIndex(index: number): void {
    this.myIndex = index;
    this.render();
  }

  public setState(state: SnakeState): void {
    this.state = state;
    if (state.roomCode && !this.roomCode) {
      this.roomCode = state.roomCode;
      this.roomCodeText.textContent = state.roomCode;
    }
    this.render();
  }

  private bindEvents(): void {
    this.createRoomButton.addEventListener("click", () => this.events.onCreateRoom());

    this.enterRoomButton.addEventListener("click", () => this.submitRoomCode());
    this.roomCodeInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") this.submitRoomCode();
    });
    this.roomCodeInput.addEventListener("input", () => {
      this.roomCodeInput.value = this.sanitizeRoomCode(this.roomCodeInput.value);
    });

    this.copyLinkButton.addEventListener("click", () => {
      void this.copyShareLink();
    });

    this.shareLinkInput.addEventListener("focus", () => this.shareLinkInput.select());

    this.joinButton.addEventListener("click", () => {
      const name = this.sanitizeName(this.nameInput.value);
      if (!name) {
        this.setMessage("Digite um nome para jogar.");
        return;
      }
      if (!this.online) {
        this.setMessage("A sala ainda não conectou ao servidor.");
        return;
      }
      this.events.onJoin(name, this.selectedColor);
    });

    this.leaveButton.addEventListener("click", () => {
      this.myIndex = -1;
      this.events.onLeave();
      this.render();
    });

    this.startButton.addEventListener("click", () => this.events.onStart());
    this.restartButton.addEventListener("click", () => this.events.onRestart());

    this.speedButtons.forEach((button) => {
      button.addEventListener("click", () => this.events.onSpeed(Number(button.dataset.speed || 1)));
    });

    this.wallButtons.forEach((button) => {
      button.addEventListener("click", () => this.events.onWalls(button.dataset.walls !== "0"));
    });

    this.dirButtons.forEach((button) => {
      const send = (event: Event): void => {
        event.preventDefault();
        this.events.onDirection(String(button.dataset.dir) as Direction);
        button.classList.add("pressed");
        window.setTimeout(() => button.classList.remove("pressed"), 90);
      };
      button.addEventListener("pointerdown", send);
      button.addEventListener("click", (event) => event.preventDefault());
    });

    window.addEventListener("keydown", (event) => {
      const map: Record<string, Direction> = {
        ArrowUp: "up",
        w: "up",
        W: "up",
        ArrowDown: "down",
        s: "down",
        S: "down",
        ArrowLeft: "left",
        a: "left",
        A: "left",
        ArrowRight: "right",
        d: "right",
        D: "right"
      };
      const direction = map[event.key];
      if (!direction) return;
      event.preventDefault();
      this.events.onDirection(direction);
    });

    this.canvasMount.addEventListener("touchstart", (event) => {
      const touch = event.changedTouches[0];
      if (!touch) return;
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
    }, { passive: true });

    this.canvasMount.addEventListener("touchend", (event) => {
      const touch = event.changedTouches[0];
      if (!touch) return;
      const dx = touch.clientX - this.touchStartX;
      const dy = touch.clientY - this.touchStartY;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);
      if (Math.max(absX, absY) < 24) return;
      this.events.onDirection(absX > absY ? (dx > 0 ? "right" : "left") : (dy > 0 ? "down" : "up"));
    }, { passive: true });
  }

  private submitRoomCode(): void {
    const code = this.sanitizeRoomCode(this.roomCodeInput.value);
    if (code.length < 4) {
      this.roomMessage.textContent = "Digite um código de sala válido.";
      return;
    }
    this.events.onEnterRoom(code);
  }

  private async copyShareLink(): Promise<void> {
    if (!this.shareUrl) return;
    try {
      await navigator.clipboard.writeText(this.shareUrl);
      this.joinMessage.textContent = "Link da sala copiado.";
    } catch {
      this.shareLinkInput.focus();
      this.shareLinkInput.select();
      this.joinMessage.textContent = "Copie o link selecionado.";
    }
  }

  private render(): void {
    this.renderViews();
    this.renderColors();
    this.renderStatus();
    this.renderLobby();
    this.renderHostControls();
    this.renderResults();
  }

  private renderViews(): void {
    const hasRoom = Boolean(this.roomCode);
    const joined = this.myIndex >= 0 && this.state?.players[this.myIndex]?.joined;
    this.roomView.classList.toggle("hidden", hasRoom);
    this.roomInfoView.classList.toggle("hidden", !hasRoom);
    this.joinView.classList.toggle("hidden", !hasRoom || Boolean(joined));
    this.gameView.classList.toggle("hidden", !joined);
  }

  private renderColors(): void {
    const colors = this.state?.colors.length ? this.state.colors : this.fallbackColors();
    const selectedIsTaken = colors.some((color) => color.id === this.selectedColor && color.taken);
    if (selectedIsTaken) this.selectedColor = colors.find((color) => !color.taken)?.id ?? 0;

    this.colorGrid.innerHTML = "";
    for (const color of colors) {
      const button = document.createElement("button");
      button.textContent = `${color.id === this.selectedColor ? "✓ " : ""}${color.name}`;
      button.style.background = color.hex;
      button.disabled = color.taken;
      button.classList.toggle("selected", color.id === this.selectedColor);
      button.addEventListener("click", () => {
        this.selectedColor = color.id;
        this.renderColors();
      });
      this.colorGrid.appendChild(button);
    }
  }

  private renderStatus(): void {
    if (!this.state) {
      this.statusLine.textContent = "Aguardando estado do jogo...";
      return;
    }

    this.statusBox.classList.toggle("running", this.state.phase === "running");
    const me = this.state.players[this.myIndex];
    const joined = this.state.players.filter((player) => player.joined).length;

    if (!me) {
      this.statusLine.textContent = "Jogador não encontrado.";
      return;
    }

    if (this.state.phase === "lobby") {
      const hostText = this.myIndex === this.state.host ? "Você é host. Inicie a partida." : "Aguarde o host iniciar.";
      this.statusLine.textContent = `Sala ${this.state.roomCode}: ${joined}/4. ${hostText} ${this.configText()}`;
      return;
    }

    if (this.state.phase === "running") {
      this.statusLine.textContent = me.alive ? "CONTINUE VIVO" : me.death || "DESCANSE EM PAZ";
      return;
    }

    this.statusLine.textContent = this.state.winner >= 0
      ? `Fim. ${this.state.players[this.state.winner]?.name || "?"} ganhou.`
      : "Fim. Empate fatal.";
  }

  private renderLobby(): void {
    const show = Boolean(this.state && this.myIndex >= 0 && this.state.phase === "lobby");
    this.lobbyList.classList.toggle("hidden", !show);
    if (!show || !this.state) return;

    const players = this.state.players.filter((player) => player.joined);
    this.lobbyList.innerHTML = players.length === 0
      ? `<div class="lobby-player"><strong>Aguardando...</strong><span></span></div>`
      : players.map((player) => this.playerLobbyRow(player)).join("");
  }

  private renderHostControls(): void {
    if (!this.state || this.myIndex < 0) {
      this.hostControls.classList.add("hidden");
      return;
    }

    const isHost = this.myIndex === this.state.host;
    this.hostControls.classList.toggle("hidden", !isHost);
    const editable = isHost && this.state.phase === "lobby";
    this.settingsHint.textContent = editable
      ? `${this.configText()} Você é o host.`
      : `${this.configText()} Alterações só no lobby.`;

    this.speedButtons.forEach((button) => {
      const level = Number(button.dataset.speed || 1);
      button.disabled = !editable;
      button.classList.toggle("selected", level === this.state?.speedLevel);
    });

    this.wallButtons.forEach((button) => {
      const enabled = button.dataset.walls !== "0";
      button.disabled = !editable;
      button.classList.toggle("selected", enabled === this.state?.walls);
    });

    this.startButton.disabled = !editable;
    this.restartButton.disabled = !isHost || this.state.phase === "running";
  }

  private renderResults(): void {
    const show = Boolean(this.state && this.myIndex >= 0 && this.state.phase === "gameover");
    this.resultCard.classList.toggle("hidden", !show);
    if (!show || !this.state) return;

    const players = this.state.players
      .filter((player) => player.joined)
      .sort((a, b) => b.len - a.len);

    this.resultSummary.innerHTML = players.map((player, index) => {
      const rank = index + 1;
      const status = player.i === this.state?.winner ? "GANHOU" : "FIM";
      return `<div class="result-row">
        <span class="rank">#${rank}</span>
        <strong style="color:${player.hex}">${this.escape(player.name)}</strong>
        <span class="meta">${player.len} | ${status}</span>
      </div>`;
    }).join("");
  }

  private playerLobbyRow(player: PlayerState): string {
    const host = player.i === this.state?.host ? " HOST" : "";
    const you = player.i === this.myIndex ? " VOCÊ" : "";
    const status = player.connected ? "ONLINE" : "OFF";
    return `<div class="lobby-player">
      <strong style="color:${player.hex}">${this.escape(player.name)}${host}${you}</strong>
      <span>${status}</span>
    </div>`;
  }

  private configText(): string {
    const level = this.state?.speedLevel ?? 1;
    const speed = level === 0 ? "Fácil" : level === 1 ? "Médio" : "Difícil";
    const walls = this.state?.walls === false ? "Sem parede" : "Com parede";
    return `Modo: ${speed}. ${walls}.`;
  }

  private sanitizeName(value: string): string {
    return value.replace(/[|"\\]/g, "").trim().slice(0, 10);
  }

  private sanitizeRoomCode(value: string): string {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
  }

  private escape(value: string): string {
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  private fallbackColors(): ColorState[] {
    return [
      { id: 0, name: "AZUL", hex: "#89B4FA", taken: false },
      { id: 1, name: "ROXO", hex: "#CBA6F7", taken: false },
      { id: 2, name: "VERDE", hex: "#A6E3A1", taken: false },
      { id: 3, name: "PESSEGO", hex: "#FAB387", taken: false }
    ];
  }

  private byId<T extends HTMLElement>(id: string): T {
    const element = document.getElementById(id);
    if (!element) throw new Error(`Elemento #${id} não encontrado.`);
    return element as T;
  }
}
